import type { VisitCounselView } from '@/entities/visit-counsel/model/visit-counsel.types';
import { hasObservingSessionMovingItemsConfirmed } from '@/entities/visit-counsel/model/observing-session-moving-items';
import { resolveRoomObservingSessionForSketchRoom } from '@/entities/visit-counsel/model/resolveRoomObservingSessionForSketchRoom';
import { isFormalAdditionalSpaceType } from '@/entities/visit-counsel/model/formalAdditionalSpace';
import { hasCompletedMovingTypesProgressStep } from '@/entities/visit-counsel/model/intake-helpers';

/**
 * "상담 완료 조건" — 상담 종료 API(`completeVisitCounsel`)를 호출할 수 있는 상태인지 판정.
 *
 * 충족 기준:
 *   1. `counsel.completedAt` 이 이미 설정되어 있으면 즉시 true (이미 종료된 상담).
 *   2. 또는 다음 모두 충족:
 *      - 진행 단계 데이터: `movingDate`, `departureMovingMethod`, `arrivalAddress`,
 *        `arrivalMovingMethod`, `packing`, 이사유형 step 완료(`hasCompletedMovingTypesProgressStep`),
 *        `customerRequests` (`questions` / `paidQuoteRequests` 배열).
 *      - 집 스케치 영상 (`sessions.sketching.video`).
 *      - 모든 메인 방: 짐 기록 confirmed + `movingItem.serviceSet` 전부 채워짐 + 영상.
 *      - 모든 추가공간 (`canRecordMovingItems === true`): 짐 기록 confirmed +
 *        `movingItem.serviceSet` 전부 채워짐 + 영상.
 *      - 추가공간 (`canRecordMovingItems === false`): 영상만 필수.
 *
 * (`buildCounselChecklist` + `getNextCounselAction === null` 와 동일한 의미. 의존성을 가볍게
 * 가져가기 위해 별도 predicate 로 분리.)
 */
export function isCounselReadyForCompletion(counsel: VisitCounselView | null | undefined): boolean {
  if (!counsel) return false;
  if (counsel.completedAt) return true;

  // 진행 단계 데이터 ─ resolveCounselFlowStage 가 stage='done' 으로 보낼 조건과 동일.
  if (!counsel.movingDate) return false;
  if (!counsel.departureMovingMethod) return false;
  if (!counsel.arrivalAddress) return false;
  if (!counsel.arrivalMovingMethod) return false;
  if (!counsel.packing) return false;

  if (!hasCompletedMovingTypesProgressStep(counsel)) return false;

  const cr = counsel.customerRequests;
  if (!cr || !Array.isArray(cr.questions) || !Array.isArray(cr.paidQuoteRequests)) {
    return false;
  }

  // 집 스케치 영상.
  const sketching = counsel.sessions?.sketching ?? null;
  if (!sketching?.video) return false;

  const rooms = sketching.rooms ?? [];
  // resolveCounselFlowStage.remainingTargets 와 동일한 기준으로 메인 방을 필터링한다.
  // - isAdditionalSpace=true 인 방은 제외 (백엔드가 추가공간으로 분류)
  // - BALCONY / RESIDENCE_STORAGE 같은 공식 추가공간 type 은 기본적으로 추가공간
  //   picker 에서 처리되므로 제외한다.
  //   단, isAdditionalSpace === false 이면 백엔드가 ROOM_OBSERVING 세션을 붙인 것이므로
  //   type 이 BALCONY 여도 primaryRooms 에 포함해야 완료 조건을 올바르게 충족한다.
  const primaryRooms = rooms.filter(
    (r) =>
      r.isAdditionalSpace === false ||
      (!r.isAdditionalSpace && !isFormalAdditionalSpaceType(r.type))
  );
  const roomObserving = counsel.sessions?.roomObserving ?? [];

  // 메인 방: 짐 confirmed + serviceSet + 영상.
  for (const room of primaryRooms) {
    const session = resolveRoomObservingSessionForSketchRoom(room, rooms, roomObserving);
    if (!hasObservingSessionMovingItemsConfirmed(session)) return false;
    if (!session?.video) return false;
    const items = session.movingItems ?? [];
    for (const it of items) {
      if (it.serviceSet == null) return false;
    }
  }

  // 추가공간.
  const additional = counsel.sessions?.additionalSpaceObserving ?? [];
  for (const space of additional) {
    if (!space.video) return false;
    if (space.canRecordMovingItems !== true) continue;
    if (!hasObservingSessionMovingItemsConfirmed(space)) return false;
    const items = space.movingItems ?? [];
    for (const it of items) {
      if (it.serviceSet == null) return false;
    }
  }

  return true;
}
